import * as React from 'react';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useRestaurantsService } from '@restworld/data-services';
import { restaurantDetailsAtom, ShowcaseSectionTitle } from '.';
import { customImageLoader, imageVariant } from '@restworld/utils-common';
import { SkeletonRectangularImageLoading, ImagePlaceholder } from '@restworld/ui-components';

const useRestaurantEmployees = ({ restaurantId }: { restaurantId?: string }) => {
  const restaurantService = useRestaurantsService();
  const queryFn = React.useCallback(async () => {
    const response = (await restaurantService.fetchRestaurantEmployees(restaurantId as string))?.data?.data;
    return response;
  }, [restaurantId, restaurantService]);
  const queryKey = React.useMemo(() => ['restaurant-employees', restaurantId], [restaurantId]);

  return useQuery({
    queryKey,
    queryFn,
    retry: 2,
    enabled: !!restaurantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IVenueTeamsProps {}

const VenueTeams: React.FunctionComponent<IVenueTeamsProps> = (props) => {
  const restaurantDetails = useAtomValue(restaurantDetailsAtom);
  const { data, isLoading } = useRestaurantEmployees({ restaurantId: restaurantDetails?.restaurantId });
  const { t } = useTranslation('restaurant-showcase-v2');
  return (
    !!data?.length && (
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-4">
        <ShowcaseSectionTitle title={t('our_team')} />
        <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap content-start">
          {isLoading &&
            Array.from({ length: 4 }).map((_, idx) => (
              <SkeletonRectangularImageLoading className="max-w-[6rem] min-w-[6rem] h-24" isIcon key={idx} />
            ))}
          {data?.map((employee) => (
            <div key={employee.id} className="inline-flex flex-col justify-start items-start gap-2">
              <span className="w-24 h-24 relative rounded-lg flex justify-start items-center overflow-hidden">
                {employee?.picture ? (
                  <Image
                    fill
                    alt={employee.full_name ?? 'profile'}
                    className="object-cover object-top"
                    src={imageVariant(employee.picture, 'sm') ?? employee.picture}
                    loader={customImageLoader}
                  />
                ) : (
                  <ImagePlaceholder className="w-full h-full" iconClasses="h-10 w-10 text-purple-700" />
                )}
              </span>
              <h5 className="justify-start text-neutral-700 text-base font-normal">{employee.full_name}</h5>
              <h5 className="justify-start text-neutral-700 text-base font-semibold w-10/12 truncate">
                {employee.occupation}
              </h5>
            </div>
          ))}
        </div>
      </div>
    )
  );
};

export default VenueTeams;
